/**
 * Contract Detail Page - T167
 *
 * View and manage a single contract
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { GlassPanel, Button, LoadingSpinner, PageHeader } from '../components/ui';
import { ContractStatusBadge } from '../components/contracts/ContractStatusBadge';
import { GenerateInvoiceButton } from '../components/contracts/GenerateInvoiceButton';
import {
  useContract,
  useActivateContract,
  usePauseContract,
  useCancelContract,
  billingIntervalLabels,
} from '../hooks/useContracts';
import { formatCurrency, formatDate } from '@armard/shared';
import { useToastHelpers } from '../components/ui';

export const Route = createFileRoute('/contracts_/$id')({
  component: ContractDetailPage,
});

function ContractDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToastHelpers();

  const { data: contract, isLoading, error } = useContract(id);
  const activateContract = useActivateContract();
  const pauseContract = usePauseContract();
  const cancelContract = useCancelContract();

  if (isLoading) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  if (error || !contract) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="text-center py-12">
            <h2 className="text-lg text-red-400">Vertrag nicht gefunden</h2>
            <Button className="mt-4" onClick={() => navigate({ to: '/contracts' })}>
              Zurück zur Liste
            </Button>
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  // Calculate totals
  const items = contract.items || [];
  const totalValue = items.reduce((sum, item) => {
    return sum + parseFloat(item.quantity) * parseFloat(item.unitPrice);
  }, 0);
  const vatRate = parseFloat(contract.vatRate);
  const vatAmount = totalValue * (vatRate / 100);
  const grossTotal = totalValue + vatAmount;

  const handleActivate = async () => {
    try {
      await activateContract.mutateAsync(contract.id);
      toast.success('Vertrag aktiviert');
    } catch {
      toast.error('Fehler beim Aktivieren');
    }
  };

  const handlePause = async () => {
    try {
      await pauseContract.mutateAsync(contract.id);
      toast.success('Vertrag pausiert');
    } catch {
      toast.error('Fehler beim Pausieren');
    }
  };

  const handleCancel = async () => {
    if (!confirm('Möchten Sie diesen Vertrag wirklich stornieren?')) return;
    try {
      await cancelContract.mutateAsync({ id: contract.id });
      toast.success('Vertrag storniert');
    } catch {
      toast.error('Fehler beim Stornieren');
    }
  };

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header — 006b P2: shared PageHeader (backTo-Eyebrow-Kanon) */}
        <PageHeader
          backTo="/contracts"
          backLabel="Verträge"
          title={contract.title}
          titleAccessory={<ContractStatusBadge status={contract.status} />}
          subtitle={contract.contractNumber}
          actions={
            <>
              {(contract.status === 'draft' || contract.status === 'paused') && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    navigate({ to: '/contracts/$id/edit', params: { id: contract.id } })
                  }
                >
                  Bearbeiten
                </Button>
              )}
              {contract.status === 'draft' && (
                <Button onClick={handleActivate} disabled={activateContract.isPending}>
                  Aktivieren
                </Button>
              )}
              {contract.status === 'active' && (
                <>
                  <GenerateInvoiceButton contract={contract} />
                  <Button
                    variant="secondary"
                    onClick={handlePause}
                    disabled={pauseContract.isPending}
                  >
                    Pausieren
                  </Button>
                </>
              )}
              {contract.status === 'paused' && (
                <Button onClick={handleActivate} disabled={activateContract.isPending}>
                  Fortsetzen
                </Button>
              )}
              {(contract.status === 'draft' ||
                contract.status === 'active' ||
                contract.status === 'paused') && (
                <Button variant="danger" onClick={handleCancel} disabled={cancelContract.isPending}>
                  Stornieren
                </Button>
              )}
            </>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer & Period */}
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Details</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-white/40">Kunde</p>
                  <p className="text-white">{contract.customer?.companyName || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-white/40">Kundennummer</p>
                  <p className="text-white">{contract.customer?.customerNumber || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-white/40">Vertragsbeginn</p>
                  <p className="text-white">{formatDate(contract.startDate)}</p>
                </div>
                <div>
                  <p className="text-sm text-white/40">Vertragsende</p>
                  <p className="text-white">
                    {contract.endDate ? formatDate(contract.endDate) : 'Unbefristet'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-white/40">Abrechnungsintervall</p>
                  <p className="text-white">{billingIntervalLabels[contract.billingInterval]}</p>
                </div>
                <div>
                  <p className="text-sm text-white/40">Abrechnungstag</p>
                  <p className="text-white">{contract.billingDayOfMonth}. des Monats</p>
                </div>
                {contract.customerReference && (
                  <div className="col-span-2">
                    <p className="text-sm text-white/40">Kundenreferenz</p>
                    <p className="text-white">{contract.customerReference}</p>
                  </div>
                )}
                {contract.description && (
                  <div className="col-span-2">
                    <p className="text-sm text-white/40">Beschreibung</p>
                    <p className="text-white whitespace-pre-wrap">{contract.description}</p>
                  </div>
                )}
              </div>
            </GlassPanel>

            {/* Items */}
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Positionen</h2>
              <div className="space-y-3">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start justify-between p-3 bg-white/5 rounded-lg"
                  >
                    <div className="flex-1">
                      <p className="text-white">{item.description}</p>
                      {item.longDescription && (
                        <p className="text-sm text-white/50 mt-1">{item.longDescription}</p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-white">
                        {parseFloat(item.quantity)} {item.unit} ×{' '}
                        {formatCurrency(parseFloat(item.unitPrice))}
                      </p>
                      <p className="text-white/70">
                        {formatCurrency(parseFloat(item.quantity) * parseFloat(item.unitPrice))}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Netto</span>
                  <span className="text-white">{formatCurrency(totalValue)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-white/50">MwSt. ({vatRate}%)</span>
                  <span className="text-white">{formatCurrency(vatAmount)}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold mt-2">
                  <span className="text-white">Gesamt</span>
                  <span className="text-white">{formatCurrency(grossTotal)}</span>
                </div>
                <p className="text-xs text-white/40 mt-1 text-right">pro Abrechnungsperiode</p>
              </div>
            </GlassPanel>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Billing Info */}
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Abrechnung</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-white/40">Nächste Rechnung</p>
                  <p className="text-white text-lg">
                    {contract.nextBillingDate ? formatDate(contract.nextBillingDate) : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-white/40">Letzte Rechnung</p>
                  <p className="text-white">
                    {contract.lastBillingDate ? formatDate(contract.lastBillingDate) : 'Noch keine'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-white/40">Automatisch erstellen</p>
                  <p className="text-white">{contract.autoGenerate ? 'Ja' : 'Nein'}</p>
                </div>
              </div>
            </GlassPanel>

            {/* Quick Actions */}
            <GlassPanel>
              <h2 className="text-lg font-medium text-white mb-4">Aktionen</h2>
              <div className="space-y-2">
                <Button
                  variant="secondary"
                  className="w-full justify-start"
                  onClick={() => navigate({ to: '/contracts' })}
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  Zur Übersicht
                </Button>
              </div>
            </GlassPanel>
          </div>
        </div>
      </AppShell>
    </ProtectedRoute>
  );
}
